// import node module libraries
// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { Link } from 'react-router-dom';
import { Col, Form, Row, Table } from 'react-bootstrap';
import {
	useAsyncDebounce,
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
// import utility file
import SuivipaiementService from '../../services/SuivipaiementService';
import ParentService from '../../services/ParentService';
import EleveService from '../../services/EleveService';
import {
	CheckCircleFill,
	ExclamationTriangleFill
} from 'react-bootstrap-icons';
import AnneescolaireService from '../../services/AnneescolaireService';

const SuivipaiementsListItems = () => {
	const [originalSuivipaiements, setOriginalSuivipaiements] = useState([]);
	const [suivipaiements, setSuivipaiements] = useState([]);
	const [parent, setParent] = useState([]); // Add this line
	const [anneescolaire, setAnneescolaire] = useState([]); // Add this line
	const [parentOptions, setParentOptions] = useState([]);
	const [eleve, setEleve] = useState([]); // Add this line
	const [eleveOptions, setEleveOptions] = useState([]);

	useEffect(() => {
		const fetchParentOptions = async () => {
			try {
				const parents = await ParentService.getParentOptions();
				setParentOptions(parents);
			} catch (error) {
				console.error('Error fetching class options', error);
			}
		};

		fetchParentOptions();
	}, []);

	const PARENT_CHOICES = parentOptions;

	useEffect(() => {
		Promise.all([
			SuivipaiementService.getAllSuivipaiements(),
			ParentService.getAllParents(),
			AnneescolaireService.getAllAnneescolaire(),
			EleveService.getAllEleves()
		]).then(([suivipaiementsData, parentData, anneeData, eleveData]) => {
			setOriginalSuivipaiements(suivipaiementsData);
			setSuivipaiements(suivipaiementsData);
			setParent(parentData);
			setEleve(eleveData);
			setAnneescolaire(anneeData);
		});
	}, []);

	useEffect(() => {
		const fetchEleveOptions = async () => {
			try {
				const eleves = await EleveService.getEleveOptions();
				setEleveOptions(eleves);
			} catch (error) {
				console.error('Error fetching eleve options', error);
			}
		};

		fetchEleveOptions();
	}, []);

	const Eleve_CHOICES = eleveOptions;

	useEffect(() => {
		Promise.all([
			SuivipaiementService.getAllSuivipaiements(),
			EleveService.getAllEleves()
		]).then(([SuivipaiementsData, eleveData]) => {
			setOriginalSuivipaiements(SuivipaiementsData);
			setSuivipaiements(SuivipaiementsData);
			setEleve(eleveData);
		});
	}, []);

	React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	const formatValue = (value, columnId) => {
		if (columnId === 'parent') {
			return value && value.nom ? value.nom.toUpperCase() : '';
		} else if (columnId === 'eleve') {
			return value && value.nom && value.prenom
				? `${value.nom.toUpperCase()} ${value.prenom
						.charAt(0)
						.toUpperCase()}${value.prenom.slice(1).toLowerCase()}`
				: '';
		} else if (
			[
				'inscription_payee',
				'tenue_classe_payee',
				'tenue_sport_payee',
				'pull_payee',
				'tranche_1_payee',
				'tranche_2_payee',
				'tranche_3_payee'
			].includes(columnId)
		) {
			return (
				<div className="d-flex justify-content-center align-items-center">
					{value ? (
						<CheckCircleFill size={25} className="me-1 text-success" />
					) : (
						<ExclamationTriangleFill size={25} className="me-1 text-warning" />
					)}
				</div>
			);
		} else if (typeof value === 'string') {
			if (columnId === 'eleve' && value) {
				return value.toUpperCase();
			} else if (columnId === 'parent' || (columnId === 'parent' && value)) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		}
		return value;
	};
	const getParentById = (parentId) => {
		return parent.find((item) => item.id === parentId) ?? {};
	};

	const getAnneescolaireById = (anneescolaireId) => {
		const anneeScolaireObj =
			anneescolaire.find((item) => item.id === anneescolaireId) ?? {};

		return anneeScolaireObj
			? anneeScolaireObj.annee_debut + ' - ' + anneeScolaireObj.annee_fin
			: '';
	};

	const globalFilterFn = (rows, ids, filterValue) => {
		// Convert the filterValue to lowercase for case-insensitive search
		const searchTerms = filterValue.toLowerCase().split(' ').filter(Boolean); // Split by spaces and remove empty strings

		// Loop through each row in the table
		return rows.filter((row) => {
			const eleve = row.original.eleve; // Access the eleve object in the row

			// Check if every term matches either the nom or prenom of the eleve
			return searchTerms.every(
				(term) =>
					eleve?.nom?.toLowerCase().includes(term) || // Check in nom
					eleve?.prenom?.toLowerCase().includes(term) // Check in prenom
			);
		});
	};

	const getEleveById = (eleveId) => {
		return eleve.find((item) => item.id === eleveId) ?? {};
	};
	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'eleve',
				Header: 'Eleve',
				Cell: ({ value, row }) => {
					return (
						<div className="d-flex align-items-center">
							{value ? (
								<Link
									to={`/finances/scolarite/suivipaiement/${row.original.id}`}
								>
									<h5 className="mb-0 ms-2">{formatValue(value, 'eleve')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},
			{
				accessor: 'anneescolaire',
				Header: 'Année scolaire',
				Cell: ({ value }) => <div>{value}</div>
			},
			{
				accessor: 'inscription_payee',
				Header: 'Inscription',
				Cell: ({ value }) => (
					<div>{formatValue(value, 'inscription_payee')}</div>
				)
			},
			{
				accessor: 'tenue_classe_payee',
				Header: 'Tenue de Classe',
				Cell: ({ value }) => (
					<div>{formatValue(value, 'tenue_classe_payee')}</div>
				)
			},
			{
				accessor: 'pull_payee',
				Header: 'Pull',
				Cell: ({ value }) => <div>{formatValue(value, 'pull_payee')}</div>
			},
			{
				accessor: 'tenue_sport_payee',
				Header: 'Tenue de sport',
				Cell: ({ value }) => (
					<div>{formatValue(value, 'tenue_sport_payee')}</div>
				)
			},
			{
				accessor: 'tranche_1_payee',
				Header: 'Premiere Tranche',
				Cell: ({ value }) => <div>{formatValue(value, 'tranche_1_payee')}</div>
			},
			{
				accessor: 'tranche_2_payee',
				Header: 'Deuxieme Tranche',
				Cell: ({ value }) => <div>{formatValue(value, 'tranche_2_payee')}</div>
			},
			{
				accessor: 'tranche_3_payee',
				Header: 'Troisieme tranche',
				Cell: ({ value }) => <div>{formatValue(value, 'tranche_3_payee')}</div>
			}
		],
		[]
	);
	const data = useMemo(() => {
		return suivipaiements.map((suivipaiement) => ({
			...suivipaiement,
			parent: getParentById(suivipaiement.parent),
			eleve: getEleveById(suivipaiement.eleve),
			anneescolaire: getAnneescolaireById(suivipaiement.anneescolaire)
		}));
	}, [suivipaiements]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			},
			globalFilter: globalFilterFn
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		console.log(filterTerm);
		console.log(originalSuivipaiements);
		if (filterTerm !== '') {
			const newSuivipaiementsList = originalSuivipaiements.filter(
				(suivipaiement) => {
					return suivipaiement.parent === Number(filterTerm);
				}
			);
			setSuivipaiements(newSuivipaiementsList);
		} else {
			setSuivipaiements(originalSuivipaiements);
		}
	};

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={useAsyncDebounce(setGlobalFilter, 200)}
							placeholder="Rechercher un élève"
						/>
					</Col>
					<Col xxl={3} lg={4} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par parent"
							options={PARENT_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default SuivipaiementsListItems;
